import React from 'react';
import { Link } from 'react-router-dom';
import { Plane, Train, ArrowRight } from 'lucide-react';
import SubpageShell from './SubpageShell';
import JsonLd, { SITE, PROVIDER } from '../components/JsonLd';
import SEO from '../components/SEO';
import '../styles/ServiceEnquiry.css';
import '../styles/Academy.css';
import SimulatorGallery from '../components/academy/SimulatorGallery';
import { InterestFact } from '../components/academy/WorkshopFacts';

/**
 * /academy — public landing for Flight Academy + Rail Academy.
 * Spec: public pages describe the pathways; the ONLY call to action is
 * "Register interest". No paid bookings, no fixed dates, no school contracts.
 */
const Academy = () => (
  <SubpageShell currentPath="/academy">
    <SEO
      title="N3URO Academy — Where curiosity becomes opportunity"
      description="N3URO Academy: neuro-inclusive Flight and Rail learning pathways, open now. Start the online lessons and register interest in on-site simulator sessions."
      canonical="/academy"
      image="https://n3uro.world/img/share-academy.jpg"
    />
    <JsonLd data={{ '@type': 'WebPage', name: 'N3URO Academy', url: `${SITE}/academy`, isPartOf: { '@id': `${SITE}/#website` }, publisher: PROVIDER }} />
    <main className="subpage-hero">
      <span className="subpage-eyebrow">N3URO Academy</span>
      <h1 className="subpage-title">Where curiosity becomes opportunity.</h1>
      <p className="subpage-lede">
        Two neuro-inclusive learning pathways, open now: <strong>Flight Academy</strong> and
        <strong> Rail Academy</strong>. Members can start the online lessons today. Our on-site simulator
        sessions are next. Register your interest and we&rsquo;ll let you know as soon as dates open.
      </p>

      <ul className="conv-facts" aria-label="Programme format" data-testid="academy-facts">
        <li><strong>90-minute workshops</strong><span>Selective, on-site simulator sessions</span></li>
        <InterestFact />
      </ul>

      <section className="service-grid" aria-label="Academy pathways">
        <Link to="/academy/flight" className="service-card" style={{ '--accent': '#5BE9FF' }} data-testid="card-academy-flight">
          <img className="service-card-media ac-card-media-full" src="/img/academy-flight-thumb.webp" alt="" loading="lazy" data-testid="card-academy-flight-image" />
          <span className="service-card-icon" aria-hidden="true"><Plane size={22} strokeWidth={1.75} /></span>
          <h3>Flight Academy</h3>
          <p>From "My First Flight" to flight systems and simulation — in Discover, Explore and Advance levels.</p>
          <span className="service-card-link">Discover Flight Academy <ArrowRight size={14} strokeWidth={2.4} /></span>
        </Link>

        <Link to="/academy/rail" className="service-card" style={{ '--accent': '#FFB166' }} data-testid="card-academy-rail">
          <img className="service-card-media ac-card-media-full" src="/img/academy-rail-thumb.webp" alt="" loading="lazy" data-testid="card-academy-rail-image" />
          <span className="service-card-icon" aria-hidden="true"><Train size={22} strokeWidth={1.75} /></span>
          <h3>Rail Academy</h3>
          <p>From "My First Train Journey" to railway systems and simulation — in Discover, Explore and Advance levels.</p>
          <span className="service-card-link">Discover Rail Academy <ArrowRight size={14} strokeWidth={2.4} /></span>
        </Link>
      </section>

      <section className="ac-gallery" aria-label="A look inside the simulators" data-testid="academy-gallery">
        <h2 className="ac-gallery-title">A look inside the simulators</h2>
        <p className="ac-gallery-lede">A first look at the spaces, so your child knows what to expect. Tap a photo to see it larger.</p>
        <SimulatorGallery pathway="flight" compact />
        <SimulatorGallery pathway="rail" compact />
      </section>

      <section style={{ marginTop: '2rem' }} data-testid="academy-ctas">
        <div className="conv-cta-row">
          <Link to="/academy/register-interest" className="conv-btn-primary" data-cta="academy-family-register" data-testid="cta-academy-register">
            Register your family&rsquo;s interest <ArrowRight size={16} strokeWidth={2.4} />
          </Link>
          <Link to="/academy/institutions" className="conv-btn-ghost" data-cta="academy-institution-enquiry" data-testid="cta-academy-institutions">
            Enquire as a school or organisation
          </Link>
        </div>
        <p className="service-enquiry-note" style={{ marginTop: '0.65rem', textAlign: 'left' }}>
          Registering is free. It is not a booking and no payment is taken.
        </p>
      </section>
    </main>
  </SubpageShell>
);

export default Academy;
